import React, { useEffect, useState, useCallback } from 'react';
import {
  collection, query, orderBy, getDocs,
  addDoc, deleteDoc, doc, serverTimestamp,
  setDoc, Timestamp, updateDoc, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getItemStatus, formatEpisodeLabel, isEpisodeConfirmed, isEpisodeMissing, formatAirDate } from '../utils/dateUtils';
import { fetchEpisodesForShow } from '../utils/mediaUtils';
import MediaCard from '../components/MediaCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const TMDB_BASE = 'https://api.themoviedb.org/3';

/** Converts a "YYYY-MM-DD" string to a Firestore Timestamp at local midnight. */
function dateStringToTimestamp(dateStr) {
  if (!dateStr) return null;
  return Timestamp.fromDate(new Date(dateStr + 'T00:00:00'));
}

/**
 * Fetches US digital (streaming) and physical (DVD/Blu-ray) release dates
 * for a movie from TMDB. Returns YYYY-MM-DD strings or null if not found.
 * TMDB release type 4 = Digital, type 5 = Physical.
 */
async function fetchMovieReleaseDates(tmdbId, tmdbKey) {
  const res = await fetch(`${TMDB_BASE}/movie/${tmdbId}/release_dates?api_key=${tmdbKey}`);
  if (!res.ok) return { streamingDate: null, dvdDate: null };
  const data = await res.json();
  const usEntry = (data.results || []).find(r => r.iso_3166_1 === 'US');
  if (!usEntry) return { streamingDate: null, dvdDate: null };
  const releaseDates = usEntry.release_dates || [];
  const digital = releaseDates.find(r => r.type === 4);
  const physical = releaseDates.find(r => r.type === 5);
  const toDateStr = (isoStr) => isoStr ? isoStr.slice(0, 10) : null;
  return {
    streamingDate: toDateStr(digital?.release_date),
    dvdDate: toDateStr(physical?.release_date),
  };
}

async function fetchTvEpisodes(tmdbId, tmdbKey) {
  const detailsRes = await fetch(`${TMDB_BASE}/tv/${tmdbId}?api_key=${tmdbKey}`);
  if (!detailsRes.ok) return { releaseDate: null, episodes: [] };

  const details = await detailsRes.json();
  const firstAirDate = details.first_air_date || null;
  const releaseDate = firstAirDate ? Timestamp.fromDate(new Date(firstAirDate)) : null;

  // Get all non-special seasons (season_number > 0)
  const seasonNumbers = (details.seasons || [])
    .filter(s => s.season_number > 0)
    .map(s => s.season_number);

  if (seasonNumbers.length === 0) {
    return { releaseDate, episodes: [] };
  }

  // Fetch all seasons in parallel
  const seasonResults = await Promise.all(
    seasonNumbers.map(async (seasonNum) => {
      const res = await fetch(`${TMDB_BASE}/tv/${tmdbId}/season/${seasonNum}?api_key=${tmdbKey}`);
      if (!res.ok) return [];
      const season = await res.json();
      return (season.episodes || [])
        .filter(ep => ep.air_date)
        .map(ep => ({
          seasonNumber: ep.season_number,
          episodeNumber: ep.episode_number,
          title: ep.name || '',
          airDate: Timestamp.fromDate(new Date(ep.air_date)),
        }));
    })
  );

  return { releaseDate, episodes: seasonResults.flat() };
}

function Admin() {
  const [activeSection, setActiveSection] = useState(0);

  // Whitelist state
  const [whitelist, setWhitelist] = useState([]);
  const [loadingWhitelist, setLoadingWhitelist] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [whitelistMsg, setWhitelistMsg] = useState('');

  // Content state
  const [catalogue, setCatalogue] = useState([]);
  const [loadingCatalogue, setLoadingCatalogue] = useState(true);
  const [catalogueSearch, setCatalogueSearch] = useState('');
  const [tmdbQuery, setTmdbQuery] = useState('');
  const [tmdbResults, setTmdbResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [tmdbError, setTmdbError] = useState('');
  const [contentMsg, setContentMsg] = useState('');
  const [addingItem, setAddingItem] = useState(null);

  // Issues / Requests state
  const [allIssues, setAllIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(true);
  const [allRequests, setAllRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [addingRequest, setAddingRequest] = useState(null);
  const [requestsMsg, setRequestsMsg] = useState('');

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onConfirm: null });

  // Movie dates dialog (shown when adding a movie to collect release dates)
  const [movieDatesDialog, setMovieDatesDialog] = useState({
    open: false, item: null, streamingDate: '', dvdDate: '', requestId: null,
  });

  // Edit movie dates dialog (shown when editing an existing movie's release dates)
  const [editDatesDialog, setEditDatesDialog] = useState({
    open: false, item: null, cinemaDate: '', streamingDate: '', dvdDate: '',
  });

  // TV episodes dialog (shown when clicking a TV show card)
  const [tvEpisodesDialog, setTvEpisodesDialog] = useState({
    open: false, itemId: null,
  });

  // Pending tab selection state
  const [selectedPendingIds, setSelectedPendingIds] = useState(new Set()); // item.id for catalogue items
  const [selectedEpisodeKeys, setSelectedEpisodeKeys] = useState(new Set()); // `${item.id}-${seasonNumber}-${episodeNumber}`

  // Pending tab expand state for TV episodes grouped view
  const [expandedShowIds, setExpandedShowIds] = useState(new Set());
  const [expandedSeasonKeys, setExpandedSeasonKeys] = useState(new Set());

  const tmdbKey = process.env.REACT_APP_TMDB_API_KEY;

  const fetchWhitelist = useCallback(async () => {
    setLoadingWhitelist(true);
    try {
      const snap = await getDocs(collection(db, 'whitelist'));
      setWhitelist(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    finally { setLoadingWhitelist(false); }
  }, []);

  const fetchCatalogue = useCallback(async () => {
    setLoadingCatalogue(true);
    try {
      const [moviesSnap, tvSnap] = await Promise.all([
        getDocs(query(collection(db, 'movies'), orderBy('title', 'asc'))),
        getDocs(query(collection(db, 'tv'), orderBy('title', 'asc'))),
      ]);

      const movies = moviesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const tvShows = await Promise.all(
        tvSnap.docs.map(async d => {
          const show = { id: d.id, ...d.data() };
          const episodes = await fetchEpisodesForShow(d.id);
          return { ...show, episodes };
        })
      );

      const all = [...movies, ...tvShows].sort((a, b) => a.title.localeCompare(b.title));
      setCatalogue(all);
    } catch (err) { console.error(err); }
    finally { setLoadingCatalogue(false); }
  }, []);

  const fetchIssues = useCallback(async () => {
    setLoadingIssues(true);
    try {
      const q = query(collection(db, 'issues'), orderBy('reportedAt', 'desc'));
      const snap = await getDocs(q);
      setAllIssues(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    finally { setLoadingIssues(false); }
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const q = query(collection(db, 'requests'), orderBy('requestedAt', 'desc'));
      const snap = await getDocs(q);
      setAllRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    finally { setLoadingRequests(false); }
  }, []);

  useEffect(() => {
    fetchWhitelist();
    fetchCatalogue();
    fetchIssues();
    fetchRequests();
  }, [fetchWhitelist, fetchCatalogue, fetchIssues, fetchRequests]);

  const showConfirmDialog = (message, onConfirm) => {
    setConfirmDialog({ open: true, message, onConfirm });
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    try {
      await setDoc(doc(db, 'whitelist', email), { email, addedAt: serverTimestamp() });
      setNewEmail('');
      setWhitelistMsg(`${email} added to whitelist.`);
      fetchWhitelist();
    } catch (err) {
      setWhitelistMsg('Failed to add email.');
      console.error(err);
    }
  };

  const handleRemoveEmail = (email) => {
    showConfirmDialog(`Remove ${email} from whitelist?`, async () => {
      try {
        await deleteDoc(doc(db, 'whitelist', email));
        setWhitelistMsg(`${email} removed.`);
        fetchWhitelist();
      } catch (err) {
        setWhitelistMsg('Failed to remove email.');
        console.error(err);
      }
    });
  };

  const handleTmdbSearch = async (e) => {
    e.preventDefault();
    if (!tmdbQuery.trim()) return;
    if (!tmdbKey) { setTmdbError('TMDB API key not configured.'); return; }
    setSearching(true);
    setTmdbError('');
    setTmdbResults([]);
    try {
      const res = await fetch(
        `${TMDB_BASE}/search/multi?query=${encodeURIComponent(tmdbQuery)}&api_key=${tmdbKey}&page=1`
      );
      if (!res.ok) throw new Error('TMDB request failed');
      const data = await res.json();
      const results = (data.results || [])
        .filter(r => r.media_type === 'movie' || r.media_type === 'tv')
        .slice(0, 24)
        .map(r => ({
          tmdbId: r.id,
          type: r.media_type,
          title: r.title || r.name,
          posterPath: r.poster_path || null,
          backdropPath: r.backdrop_path || null,
          overview: r.overview || '',
          releaseDate: r.release_date || r.first_air_date || null,
        }));
      setTmdbResults(results);
      if (results.length === 0) setTmdbError('No results found.');
    } catch (err) {
      setTmdbError('Search failed. Please try again.');
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const handleAddToCollection = async (item, options = {}) => {
    const { requestId = null, confirmed } = options;
    setAddingItem(item.tmdbId);
    setContentMsg('');

    try {
      let enrichedItem = { ...item };

      if (item.type === 'tv' && tmdbKey) {
        const tvData = await fetchTvEpisodes(item.tmdbId, tmdbKey);
        enrichedItem = {
          ...enrichedItem,
          releaseDate: tvData.releaseDate,
          // Episodes stored in subcollections — do not put in root doc
        };

        // Build root doc, adding confirmed field only when explicitly provided
        const tvDocData = { ...enrichedItem, type: 'tv', addedAt: serverTimestamp() };
        if (confirmed !== undefined) tvDocData.confirmed = confirmed;

        // Write TV show root doc
        const docRef = await addDoc(collection(db, 'tv'), tvDocData);

        // Group episodes by season and write to subcollections using batches
        const episodesBySeason = {};
        for (const ep of tvData.episodes) {
          const s = String(ep.seasonNumber);
          if (!episodesBySeason[s]) episodesBySeason[s] = [];
          episodesBySeason[s].push(ep);
        }

        const BATCH_LIMIT = 490;
        let batch = writeBatch(db);
        let opCount = 0;

        const flushBatch = async () => {
          if (opCount > 0) {
            await batch.commit();
            batch = writeBatch(db);
            opCount = 0;
          }
        };

        for (const [seasonNum, episodes] of Object.entries(episodesBySeason)) {
          batch.set(
            doc(db, 'tv', docRef.id, 'seasons', seasonNum),
            { seasonNumber: Number(seasonNum), episodeCount: episodes.length }
          );
          opCount++;
          if (opCount >= BATCH_LIMIT) await flushBatch();

          for (const ep of episodes) {
            batch.set(
              doc(db, 'tv', docRef.id, 'seasons', seasonNum, 'episodes', String(ep.episodeNumber)),
              ep
            );
            opCount++;
            if (opCount >= BATCH_LIMIT) await flushBatch();
          }
        }
        await flushBatch();

        if (requestId) {
          await updateDoc(doc(db, 'requests', requestId), { status: 'added' });
          fetchRequests();
        }

        setContentMsg(`"${item.title}" added.`);
        fetchCatalogue();
        return;
      }

      // Movie: convert releaseDate string to Timestamp if needed
      if (item.type === 'movie' && typeof enrichedItem.releaseDate === 'string' && enrichedItem.releaseDate) {
        enrichedItem.releaseDate = dateStringToTimestamp(enrichedItem.releaseDate);
      }

      // Build movie doc, adding confirmed field only when explicitly provided
      const movieDocData = { ...enrichedItem, type: 'movie', addedAt: serverTimestamp() };
      if (confirmed !== undefined) movieDocData.confirmed = confirmed;

      await addDoc(collection(db, 'movies'), movieDocData);

      if (requestId) {
        await updateDoc(doc(db, 'requests', requestId), { status: 'added' });
        fetchRequests();
      }

      setContentMsg(`"${item.title}" added.`);
      fetchCatalogue();
    } catch (err) {
      setContentMsg('Failed to add item.');
      console.error(err);
    } finally {
      setAddingItem(null);
    }
  };

  // Opens the movie dates dialog instead of adding directly.
  // For movies, pre-fetches US digital/physical release dates from TMDB.
  const handleAddClick = async (item) => {
    if (item.type === 'movie') {
      setAddingItem(item.tmdbId);
      let streamingDate = '';
      let dvdDate = '';
      if (tmdbKey) {
        try {
          const dates = await fetchMovieReleaseDates(item.tmdbId, tmdbKey);
          streamingDate = dates.streamingDate || '';
          dvdDate = dates.dvdDate || '';
        } catch (err) {
          console.error('Failed to fetch movie release dates:', err);
        }
      }
      setAddingItem(null);
      setMovieDatesDialog({ open: true, item, streamingDate, dvdDate });
    } else {
      handleAddToCollection(item);
    }
  };

  const handleMovieDatesSubmit = async () => {
    const { item, streamingDate, dvdDate, requestId } = movieDatesDialog;
    setMovieDatesDialog(d => ({ ...d, open: false }));

    const extraDates = {};
    if (streamingDate) {
      extraDates.streamingReleaseDate = dateStringToTimestamp(streamingDate);
    }
    if (dvdDate) {
      extraDates.dvdReleaseDate = dateStringToTimestamp(dvdDate);
    }

    await handleAddToCollection(
      { ...item, ...extraDates },
      requestId ? { requestId, confirmed: false } : {}
    );
  };

  // Called when "Add to Catalogue" is clicked on a request in the Requests tab.
  const handleAddRequestClick = async (req) => {
    setRequestsMsg('');

    // If the item is already in the catalogue, just mark the request as added.
    const alreadyInCatalogue = catalogue.some(
      item => item.tmdbId === req.tmdbId && item.type === req.type
    );
    if (alreadyInCatalogue) {
      try {
        await updateDoc(doc(db, 'requests', req.id), { status: 'added' });
        fetchRequests();
        setRequestsMsg(`"${req.title}" is already in the catalogue. Request marked as added.`);
      } catch (err) {
        setRequestsMsg('Failed to update request.');
        console.error(err);
      }
      return;
    }

    const item = {
      tmdbId: req.tmdbId,
      type: req.type,
      title: req.title,
      posterPath: req.posterPath || null,
      backdropPath: null,
      overview: req.overview || '',
      releaseDate: req.releaseDate || null,
    };

    if (req.type === 'movie') {
      setAddingRequest(req.id);
      let streamingDate = '';
      let dvdDate = '';
      if (tmdbKey) {
        try {
          const dates = await fetchMovieReleaseDates(req.tmdbId, tmdbKey);
          streamingDate = dates.streamingDate || '';
          dvdDate = dates.dvdDate || '';
        } catch (err) {
          console.error('Failed to fetch movie release dates:', err);
        }
      }
      setAddingRequest(null);
      setMovieDatesDialog({ open: true, item, streamingDate, dvdDate, requestId: req.id });
    } else {
      setAddingRequest(req.id);
      try {
        await handleAddToCollection(item, { requestId: req.id, confirmed: false });
      } finally {
        setAddingRequest(null);
      }
    }
  };

  const handleConfirmItem = (item) => {
    showConfirmDialog(`Confirm "${item.title}" is now available in the catalogue?`, async () => {
      try {
        const colName = item.type === 'tv' ? 'tv' : 'movies';
        await updateDoc(doc(db, colName, item.id), {
          confirmed: true,
          addedAt: serverTimestamp(),
        });
        setContentMsg(`"${item.title}" confirmed in catalogue.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to confirm item.');
        console.error(err);
      }
    });
  };

  const handleConfirmEpisode = (item, episode) => {
    const label = formatEpisodeLabel(episode);
    showConfirmDialog(`Confirm "${item.title}" ${label} is now available?`, async () => {
      try {
        await updateDoc(
          doc(db, 'tv', item.id, 'seasons', String(episode.seasonNumber), 'episodes', String(episode.episodeNumber)),
          { confirmed: true, missing: false, addedAt: serverTimestamp() }
        );
        setContentMsg(`"${item.title}" ${label} confirmed.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to confirm episode.');
        console.error(err);
      }
    });
  };

  const handleMarkEpisodeMissing = (item, episode) => {
    const label = formatEpisodeLabel(episode);
    showConfirmDialog(`Mark "${item.title}" ${label} as missing (not available)?`, async () => {
      try {
        await updateDoc(
          doc(db, 'tv', item.id, 'seasons', String(episode.seasonNumber), 'episodes', String(episode.episodeNumber)),
          { missing: true }
        );
        setContentMsg(`"${item.title}" ${label} marked as missing.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to mark episode as missing.');
        console.error(err);
      }
    });
  };

  const handleUnmarkEpisodeMissing = (item, episode) => {
    const label = formatEpisodeLabel(episode);
    showConfirmDialog(`Unmark "${item.title}" ${label} as missing?`, async () => {
      try {
        await updateDoc(
          doc(db, 'tv', item.id, 'seasons', String(episode.seasonNumber), 'episodes', String(episode.episodeNumber)),
          { missing: false }
        );
        setContentMsg(`"${item.title}" ${label} removed from missing.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to unmark episode.');
        console.error(err);
      }
    });
  };

  const handleConfirmAllAiredEpisodes = () => {
    const tvItemsWithPending = catalogue.filter(c => c.type === 'tv' && (c.episodes || []).length > 0);
    const pendingEpisodesList = tvItemsWithPending.flatMap(item =>
      (item.episodes || [])
        .filter(ep => {
          if (!ep.airDate) return false;
          const airDate = ep.airDate.toDate ? ep.airDate.toDate() : new Date(ep.airDate);
          if (airDate > new Date()) return false;
          return !isEpisodeConfirmed(ep) && !isEpisodeMissing(ep);
        })
        .map(ep => ({ item, episode: ep }))
    );

    const count = pendingEpisodesList.length;

    if (count === 0) {
      setContentMsg('No unconfirmed aired episodes to confirm.');
      return;
    }

    showConfirmDialog(
      `Confirm all ${count} previously aired episode${count !== 1 ? 's' : ''} across all TV shows?`,
      async () => {
        try {
          const BATCH_LIMIT = 500;
          let batch = writeBatch(db);
          let opCount = 0;

          for (const { item, episode } of pendingEpisodesList) {
            batch.update(
              doc(db, 'tv', item.id, 'seasons', String(episode.seasonNumber), 'episodes', String(episode.episodeNumber)),
              { confirmed: true, addedAt: serverTimestamp() }
            );
            opCount++;
            if (opCount >= BATCH_LIMIT) {
              await batch.commit();
              batch = writeBatch(db);
              opCount = 0;
            }
          }
          if (opCount > 0) await batch.commit();

          setContentMsg(`Confirmed ${count} previously aired episode${count !== 1 ? 's' : ''}.`);
          fetchCatalogue();
        } catch (err) {
          setContentMsg('Failed to bulk-confirm episodes.');
          console.error(err);
        }
      }
    );
  };

  const handleBulkConfirmSelected = (pendingItems, pendingTvEpisodes) => {
    const selectedItemsList = pendingItems.filter(i => selectedPendingIds.has(i.id));
    const selectedEpList = pendingTvEpisodes.filter(({ item, episode }) =>
      selectedEpisodeKeys.has(`${item.id}-${episode.seasonNumber}-${episode.episodeNumber}`)
    );
    const totalCount = selectedItemsList.length + selectedEpList.length;
    if (totalCount === 0) return;
    showConfirmDialog(`Confirm ${totalCount} selected item${totalCount !== 1 ? 's' : ''}?`, async () => {
      try {
        for (const item of selectedItemsList) {
          const colName = item.type === 'tv' ? 'tv' : 'movies';
          await updateDoc(doc(db, colName, item.id), { confirmed: true, addedAt: serverTimestamp() });
        }
        if (selectedEpList.length > 0) {
          const BATCH_LIMIT = 500;
          let batch = writeBatch(db);
          let opCount = 0;
          for (const { item, episode } of selectedEpList) {
            batch.update(
              doc(db, 'tv', item.id, 'seasons', String(episode.seasonNumber), 'episodes', String(episode.episodeNumber)),
              { confirmed: true, addedAt: serverTimestamp() }
            );
            opCount++;
            if (opCount >= BATCH_LIMIT) {
              await batch.commit();
              batch = writeBatch(db);
              opCount = 0;
            }
          }
          if (opCount > 0) await batch.commit();
        }
        setSelectedPendingIds(new Set());
        setSelectedEpisodeKeys(new Set());
        setContentMsg(`Confirmed ${totalCount} selected item${totalCount !== 1 ? 's' : ''}.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to confirm selected items.');
        console.error(err);
      }
    });
  };

  const handleBulkMarkMissingSelected = (pendingItems, pendingTvEpisodes) => {
    const selectedItemsList = pendingItems.filter(i => selectedPendingIds.has(i.id));
    const selectedEpList = pendingTvEpisodes.filter(({ item, episode }) =>
      selectedEpisodeKeys.has(`${item.id}-${episode.seasonNumber}-${episode.episodeNumber}`)
    );
    const totalCount = selectedItemsList.length + selectedEpList.length;
    if (totalCount === 0) return;
    showConfirmDialog(`Mark ${totalCount} selected item${totalCount !== 1 ? 's' : ''} as missing?`, async () => {
      try {
        for (const item of selectedItemsList) {
          if (item.type === 'movie') {
            await updateDoc(doc(db, 'movies', item.id), { missing: true });
          } else {
            const seasonsSnap = await getDocs(collection(db, 'tv', item.id, 'seasons'));
            const episodeSnaps = await Promise.all(
              seasonsSnap.docs.map(seasonDoc =>
                getDocs(collection(db, 'tv', item.id, 'seasons', seasonDoc.id, 'episodes'))
              )
            );
            const refsToUpdate = episodeSnaps.flatMap(snap => snap.docs.map(d => d.ref));
            const BATCH_LIMIT = 500;
            for (let i = 0; i < refsToUpdate.length; i += BATCH_LIMIT) {
              const batch = writeBatch(db);
              refsToUpdate.slice(i, i + BATCH_LIMIT).forEach(ref => { batch.update(ref, { missing: true }); });
              await batch.commit();
            }
            await updateDoc(doc(db, 'tv', item.id), { missing: true });
          }
        }
        if (selectedEpList.length > 0) {
          const BATCH_LIMIT = 500;
          let batch = writeBatch(db);
          let opCount = 0;
          for (const { item, episode } of selectedEpList) {
            batch.update(
              doc(db, 'tv', item.id, 'seasons', String(episode.seasonNumber), 'episodes', String(episode.episodeNumber)),
              { missing: true }
            );
            opCount++;
            if (opCount >= BATCH_LIMIT) {
              await batch.commit();
              batch = writeBatch(db);
              opCount = 0;
            }
          }
          if (opCount > 0) await batch.commit();
        }
        setSelectedPendingIds(new Set());
        setSelectedEpisodeKeys(new Set());
        setContentMsg(`Marked ${totalCount} selected item${totalCount !== 1 ? 's' : ''} as missing.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to mark selected items as missing.');
        console.error(err);
      }
    });
  };

  const handleRemoveFromCatalogue = (item) => {
    showConfirmDialog(`Remove "${item.title}" from catalogue?`, async () => {
      try {
        if (item.type === 'tv') {
          // Delete seasons and episodes subcollections using batched writes,
          // then remove the root TV show document.
          const seasonsSnap = await getDocs(collection(db, 'tv', item.id, 'seasons'));
          const episodeSnaps = await Promise.all(
            seasonsSnap.docs.map(seasonDoc =>
              getDocs(collection(db, 'tv', item.id, 'seasons', seasonDoc.id, 'episodes'))
            )
          );

          // Collect all refs to delete (episodes first, then seasons, then root doc)
          const refsToDelete = [
            ...episodeSnaps.flatMap(snap => snap.docs.map(d => d.ref)),
            ...seasonsSnap.docs.map(d => d.ref),
            doc(db, 'tv', item.id),
          ];

          // Delete in batches of 500
          const BATCH_LIMIT = 500;
          for (let i = 0; i < refsToDelete.length; i += BATCH_LIMIT) {
            const batch = writeBatch(db);
            refsToDelete.slice(i, i + BATCH_LIMIT).forEach(ref => batch.delete(ref));
            await batch.commit();
          }
        } else {
          await deleteDoc(doc(db, 'movies', item.id));
        }
        setContentMsg(`"${item.title}" removed.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to remove item.');
        console.error(err);
      }
    });
  };

  const handleMarkMovieMissing = (item) => {
    showConfirmDialog(`Mark "${item.title}" as missing (file unavailable)?`, async () => {
      try {
        await updateDoc(doc(db, 'movies', item.id), { missing: true });
        setContentMsg(`"${item.title}" marked as missing.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to mark movie as missing.');
        console.error(err);
      }
    });
  };

  const handleUnmarkMovieMissing = (item) => {
    showConfirmDialog(`Unmark "${item.title}" as missing?`, async () => {
      try {
        await updateDoc(doc(db, 'movies', item.id), { missing: false });
        setContentMsg(`"${item.title}" removed from missing.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to unmark movie.');
        console.error(err);
      }
    });
  };

  const handleMarkTvShowMissing = (item) => {
    showConfirmDialog(`Mark "${item.title}" as missing (file unavailable)?`, async () => {
      try {
        const seasonsSnap = await getDocs(collection(db, 'tv', item.id, 'seasons'));
        const episodeSnaps = await Promise.all(
          seasonsSnap.docs.map(seasonDoc =>
            getDocs(collection(db, 'tv', item.id, 'seasons', seasonDoc.id, 'episodes'))
          )
        );

        const refsToUpdate = episodeSnaps.flatMap(snap => snap.docs.map(d => d.ref));
        const BATCH_LIMIT = 500;
        for (let i = 0; i < refsToUpdate.length; i += BATCH_LIMIT) {
          const batch = writeBatch(db);
          refsToUpdate.slice(i, i + BATCH_LIMIT).forEach(ref => {
            batch.update(ref, { missing: true });
          });
          await batch.commit();
        }

        await updateDoc(doc(db, 'tv', item.id), { missing: true });
        setContentMsg(`"${item.title}" marked as missing.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to mark TV show as missing.');
        console.error(err);
      }
    });
  };

  const handleUnmarkTvShowMissing = (item) => {
    showConfirmDialog(`Unmark "${item.title}" as missing?`, async () => {
      try {
        await updateDoc(doc(db, 'tv', item.id), { missing: false });
        setContentMsg(`"${item.title}" removed from missing.`);
        fetchCatalogue();
      } catch (err) {
        setContentMsg('Failed to unmark TV show.');
        console.error(err);
      }
    });
  };

  const handleOpenTvEpisodesDialog = (item) => {
    if (item.type !== 'tv') return;
    setTvEpisodesDialog({ open: true, itemId: item.id });
  };

  const handleEditDatesOpen = (item) => {
    const toDateStr = (val) => {
      if (!val) return '';
      if (val.toDate) return val.toDate().toISOString().slice(0, 10);
      return String(val).slice(0, 10);
    };
    setEditDatesDialog({
      open: true,
      item,
      cinemaDate: toDateStr(item.releaseDate),
      streamingDate: toDateStr(item.streamingReleaseDate),
      dvdDate: toDateStr(item.dvdReleaseDate),
    });
  };

  const handleEditDatesSubmit = async () => {
    const { item, cinemaDate, streamingDate, dvdDate } = editDatesDialog;
    setEditDatesDialog(d => ({ ...d, open: false }));
    try {
      const updates = {
        releaseDate: cinemaDate ? dateStringToTimestamp(cinemaDate) : null,
        streamingReleaseDate: streamingDate ? dateStringToTimestamp(streamingDate) : null,
        dvdReleaseDate: dvdDate ? dateStringToTimestamp(dvdDate) : null,
      };
      await updateDoc(doc(db, 'movies', item.id), updates);
      setContentMsg(`"${item.title}" dates updated.`);
      fetchCatalogue();
    } catch (err) {
      setContentMsg('Failed to update dates.');
      console.error(err);
    }
  };

  const catalogueItems = catalogue.filter(c => {
    const s = getItemStatus(c);
    return s === 'catalogue';
  });
  const missingItems = catalogue.filter(c => getItemStatus(c) === 'missing');
  const filteredCatalogueItems = catalogueItems.filter(item =>
    item.title.toLowerCase().includes(catalogueSearch.trim().toLowerCase())
  );
  const inTheatresItems = catalogue.filter(c => getItemStatus(c) === 'in_theatres');
  const comingSoonItems = catalogue.filter(c => getItemStatus(c) === 'coming_soon');
  const pendingItems = catalogue.filter(c => getItemStatus(c) === 'pending_catalogue');
  const existingIds = new Set(catalogue.map(c => `${c.type}-${c.tmdbId}`));
  const selectedTvShow = catalogue.find(c => c.id === tvEpisodesDialog.itemId && c.type === 'tv') || null;
  const selectedTvEpisodes = selectedTvShow
    ? [...(selectedTvShow.episodes || [])].sort((a, b) => {
      const seasonDiff = (a.seasonNumber || 0) - (b.seasonNumber || 0);
      if (seasonDiff !== 0) return seasonDiff;
      return (a.episodeNumber || 0) - (b.episodeNumber || 0);
    })
    : [];

  // TV shows with individual aired episodes awaiting admin confirmation
  const now = new Date();
  const pendingTvEpisodes = catalogue
    .filter(c => c.type === 'tv')
    .flatMap(item => {
      return (item.episodes || [])
        .filter(ep => {
          if (!ep.airDate) return false;
          const airDate = ep.airDate.toDate ? ep.airDate.toDate() : new Date(ep.airDate);
          if (airDate > now) return false; // not yet aired
          return !isEpisodeConfirmed(ep) && !isEpisodeMissing(ep);
        })
        .map(ep => ({ item, episode: ep }));
    });

  // TV show episodes marked as missing by admin
  const missingTvEpisodes = catalogue
    .filter(c => c.type === 'tv')
    .flatMap(item =>
      (item.episodes || []).filter(ep => isEpisodeMissing(ep)).map(ep => ({ item, episode: ep }))
    );

  const requestStatusColor = (s) => {
    if (s === 'added' || s === 'fulfilled') return 'success';
    if (s === 'coming_soon') return 'warning';
    if (s === 'denied') return 'error';
    return 'default';
  };
  const requestStatusLabel = (s) => {
    if (s === 'coming_soon') return 'Coming Soon';
    if (s === 'added') return 'Added';
    if (!s) return 'Pending';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  const issueStatusColor = (s) => s === 'resolved' ? 'success' : s === 'in_progress' ? 'info' : 'warning';

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h5" color="primary" sx={{ mb: 3 }}>
        ⚙️ Admin Panel
      </Typography>

      <Tabs
        value={activeSection}
        onChange={(_, v) => setActiveSection(v)}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Content" />
        <Tab label="Pending" />
        <Tab label="Whitelist" />
        <Tab label="Requests" />
        <Tab label="Issues" />
        <Tab label="Missing" />
      </Tabs>

      {/* CONTENT */}
      {activeSection === 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Content Management</Typography>

          <Box component="form" onSubmit={handleTmdbSearch} sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search TMDB for a movie or TV show…"
              value={tmdbQuery}
              onChange={e => setTmdbQuery(e.target.value)}
              sx={{ flex: '1 1 260px' }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={searching || !tmdbQuery.trim()}
              startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            >
              {searching ? 'Searching…' : 'Search TMDB'}
            </Button>
          </Box>

          {tmdbError && <Alert severity="error" sx={{ mb: 2 }}>{tmdbError}</Alert>}
          {contentMsg && <Alert severity="success" sx={{ mb: 2 }}>{contentMsg}</Alert>}

          {tmdbResults.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Search Results</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {tmdbResults.map(item => {
                  const alreadyAdded = existingIds.has(`${item.type}-${item.tmdbId}`);
                  return (
                    <MediaCard
                      key={`${item.type}-${item.tmdbId}`}
                      item={item}
                      actions={
                        alreadyAdded ? (
                          <Typography variant="caption" color="text.disabled">Already added</Typography>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!!addingItem}
                            onClick={() => handleAddClick(item)}
                            sx={{ flex: 1, fontSize: '0.7rem', px: 0.5 }}
                          >
                            {addingItem === item.tmdbId ? 'Adding…' : '+ Add'}
                          </Button>
                        )
                      }
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {loadingCatalogue ? (
            <LoadingSpinner message="Loading catalogue…" />
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6">Catalogue ({filteredCatalogueItems.length})</Typography>
                <TextField
                  size="small"
                  placeholder="Search catalogue…"
                  value={catalogueSearch}
                  onChange={e => setCatalogueSearch(e.target.value)}
                  sx={{ flex: '1 1 220px', maxWidth: 320 }}
                />
              </Box>
              {filteredCatalogueItems.length === 0 ? (
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {catalogueItems.length === 0 ? 'No items in catalogue.' : 'No catalogue items match your search.'}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                  {filteredCatalogueItems.map(item => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      onClick={item.type === 'tv' ? () => handleOpenTvEpisodesDialog(item) : undefined}
                      actions={
                        <>
                          {item.type === 'movie' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              fullWidth
                              startIcon={<EditIcon fontSize="small" />}
                              onClick={() => handleEditDatesOpen(item)}
                              sx={{ fontSize: '0.7rem' }}
                            >
                              Edit Dates
                            </Button>
                          )}
                          {item.type === 'movie' && (
                            item.missing ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                fullWidth
                                onClick={() => handleUnmarkMovieMissing(item)}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                Unmark Missing
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                fullWidth
                                startIcon={<WarningAmberIcon fontSize="small" />}
                                onClick={() => handleMarkMovieMissing(item)}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                Missing
                              </Button>
                            )
                          )}
                          {item.type === 'tv' && (
                            item.missing ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                fullWidth
                                onClick={() => handleUnmarkTvShowMissing(item)}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                Unmark Missing
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                fullWidth
                                startIcon={<WarningAmberIcon fontSize="small" />}
                                onClick={() => handleMarkTvShowMissing(item)}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                Missing
                              </Button>
                            )
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<DeleteIcon fontSize="small" />}
                            onClick={() => handleRemoveFromCatalogue(item)}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            Remove
                          </Button>
                        </>
                      }
                    />
                  ))}
                </Box>
              )}

              {/* In Theatres */}
              <Typography variant="h6" sx={{ mb: 1.5 }}>🎬 In Theatres ({inTheatresItems.length})</Typography>
              {inTheatresItems.length === 0 ? (
                <Typography color="text.secondary" sx={{ mb: 3 }}>No items currently in theatres.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                  {inTheatresItems.map(item => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      actions={
                        <>
                          {item.type === 'movie' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              fullWidth
                              startIcon={<EditIcon fontSize="small" />}
                              onClick={() => handleEditDatesOpen(item)}
                              sx={{ fontSize: '0.7rem' }}
                            >
                              Edit Dates
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<DeleteIcon fontSize="small" />}
                            onClick={() => handleRemoveFromCatalogue(item)}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            Remove
                          </Button>
                        </>
                      }
                    />
                  ))}
                </Box>
              )}

              <Typography variant="h6" sx={{ mb: 1.5 }}>Coming Soon ({comingSoonItems.length})</Typography>
              {comingSoonItems.length === 0 ? (
                <Typography color="text.secondary">No coming soon items.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {comingSoonItems.map(item => (
                    <MediaCard
                      key={item.id}
                      item={item}
                      onClick={item.type === 'tv' ? () => handleOpenTvEpisodesDialog(item) : undefined}
                      actions={
                        <>
                          {item.type === 'movie' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              fullWidth
                              startIcon={<EditIcon fontSize="small" />}
                              onClick={() => handleEditDatesOpen(item)}
                              sx={{ fontSize: '0.7rem' }}
                            >
                              Edit Dates
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<DeleteIcon fontSize="small" />}
                            onClick={() => handleRemoveFromCatalogue(item)}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            Remove
                          </Button>
                        </>
                      }
                    />
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* PENDING */}
      {activeSection === 1 && (() => {
        const totalSelectable = pendingItems.length + pendingTvEpisodes.length;
        const totalSelected = selectedPendingIds.size + selectedEpisodeKeys.size;
        const allSelected = totalSelectable > 0 && totalSelected === totalSelectable;

        const handleSelectAllPending = () => {
          setSelectedPendingIds(new Set(pendingItems.map(i => i.id)));
          setSelectedEpisodeKeys(new Set(
            pendingTvEpisodes.map(({ item, episode }) =>
              `${item.id}-${episode.seasonNumber}-${episode.episodeNumber}`
            )
          ));
        };

        const handleSelectNonePending = () => {
          setSelectedPendingIds(new Set());
          setSelectedEpisodeKeys(new Set());
        };

        const togglePendingItem = (id) => {
          setSelectedPendingIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
          });
        };

        const toggleEpisodeKey = (key) => {
          setSelectedEpisodeKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
          });
        };

        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
              <Typography variant="h6">Pending Management</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {totalSelected > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    {totalSelected} selected
                  </Typography>
                )}
                <Button size="small" variant="outlined" onClick={handleSelectAllPending} disabled={totalSelectable === 0} sx={{ fontSize: '0.7rem' }}>Select All</Button>
                <Button size="small" variant="outlined" onClick={handleSelectNonePending} disabled={totalSelected === 0} sx={{ fontSize: '0.7rem' }}>Select None</Button>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon fontSize="small" />}
                  disabled={totalSelected === 0}
                  onClick={() => handleBulkConfirmSelected(pendingItems, pendingTvEpisodes)}
                  sx={{ fontSize: '0.7rem' }}
                >
                  Confirm Selected
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<WarningAmberIcon fontSize="small" />}
                  disabled={totalSelected === 0}
                  onClick={() => handleBulkMarkMissingSelected(pendingItems, pendingTvEpisodes)}
                  sx={{ fontSize: '0.7rem' }}
                >
                  Mark Missing
                </Button>
              </Box>
            </Box>

            {contentMsg && <Alert severity="success" sx={{ mb: 2 }}>{contentMsg}</Alert>}

            {loadingCatalogue ? (
              <LoadingSpinner message="Loading pending content…" />
            ) : (
              <>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  ⏳ Pending Confirmation ({pendingItems.length})
                </Typography>
                {pendingItems.length === 0 ? (
                  <Typography color="text.secondary" sx={{ mb: 3 }}>No items awaiting confirmation.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                    {pendingItems.map(item => {
                      const isSelected = selectedPendingIds.has(item.id);
                      return (
                        <Box key={`pending-${item.id}`} sx={{ position: 'relative' }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => togglePendingItem(item.id)}
                            onClick={e => e.stopPropagation()}
                            size="small"
                            sx={{
                              position: 'absolute', top: 4, right: 4, zIndex: 10,
                              bgcolor: 'rgba(0,0,0,0.55)', borderRadius: '50%',
                              p: '2px',
                              color: isSelected ? 'primary.main' : 'grey.300',
                              '&.Mui-checked': { color: 'primary.main' },
                            }}
                          />
                          <MediaCard
                            item={item}
                            onClick={item.type === 'tv' ? () => handleOpenTvEpisodesDialog(item) : undefined}
                            actions={
                              <>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  fullWidth
                                  startIcon={<CheckCircleIcon fontSize="small" />}
                                  onClick={() => handleConfirmItem(item)}
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  Confirm
                                </Button>
                                {item.type === 'movie' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                    startIcon={<EditIcon fontSize="small" />}
                                    onClick={() => handleEditDatesOpen(item)}
                                    sx={{ fontSize: '0.7rem' }}
                                  >
                                    Edit Dates
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  fullWidth
                                  startIcon={<DeleteIcon fontSize="small" />}
                                  onClick={() => handleRemoveFromCatalogue(item)}
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  Remove
                                </Button>
                              </>
                            }
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, mt: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6">
                    📺 Pending TV Episodes ({pendingTvEpisodes.length})
                  </Typography>
                  {pendingTvEpisodes.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<CheckCircleIcon fontSize="small" />}
                      onClick={handleConfirmAllAiredEpisodes}
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Confirm All Aired
                    </Button>
                  )}
                </Box>
                {pendingTvEpisodes.length === 0 ? (
                  <Typography color="text.secondary" sx={{ mb: 3 }}>No aired episodes awaiting confirmation.</Typography>
                ) : (() => {
                  // Group pendingTvEpisodes → show → season
                  const groupedShows = [];
                  const showMap = new Map();
                  for (const { item, episode } of pendingTvEpisodes) {
                    if (!showMap.has(item.id)) {
                      const entry = { item, seasons: new Map() };
                      showMap.set(item.id, entry);
                      groupedShows.push(entry);
                    }
                    const entry = showMap.get(item.id);
                    const sn = episode.seasonNumber;
                    if (!entry.seasons.has(sn)) entry.seasons.set(sn, []);
                    entry.seasons.get(sn).push(episode);
                  }

                  const toggleShowExpand = (showId) => {
                    setExpandedShowIds(prev => {
                      const next = new Set(prev);
                      if (next.has(showId)) next.delete(showId); else next.add(showId);
                      return next;
                    });
                  };

                  const toggleSeasonExpand = (key) => {
                    setExpandedSeasonKeys(prev => {
                      const next = new Set(prev);
                      if (next.has(key)) next.delete(key); else next.add(key);
                      return next;
                    });
                  };

                  const toggleShowEpisodes = (showEpKeys) => {
                    const allSel = showEpKeys.every(k => selectedEpisodeKeys.has(k));
                    setSelectedEpisodeKeys(prev => {
                      const next = new Set(prev);
                      if (allSel) showEpKeys.forEach(k => next.delete(k));
                      else showEpKeys.forEach(k => next.add(k));
                      return next;
                    });
                  };

                  const toggleSeasonEpisodes = (seasonEpKeys) => {
                    const allSel = seasonEpKeys.every(k => selectedEpisodeKeys.has(k));
                    setSelectedEpisodeKeys(prev => {
                      const next = new Set(prev);
                      if (allSel) seasonEpKeys.forEach(k => next.delete(k));
                      else seasonEpKeys.forEach(k => next.add(k));
                      return next;
                    });
                  };

                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                      {groupedShows.map(({ item, seasons }) => {
                        const showExpanded = expandedShowIds.has(item.id);
                        const sortedSeasons = [...seasons.entries()].sort(([a], [b]) => a - b);
                        const showEpKeys = sortedSeasons.flatMap(([, eps]) =>
                          eps.map(ep => `${item.id}-${ep.seasonNumber}-${ep.episodeNumber}`)
                        );
                        const showAllSel = showEpKeys.every(k => selectedEpisodeKeys.has(k));
                        const showAnySel = showEpKeys.some(k => selectedEpisodeKeys.has(k));

                        return (
                          <Paper key={`show-group-${item.id}`} variant="outlined" sx={{ overflow: 'hidden' }}>
                            {/* Show header row */}
                            <Box
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                                cursor: 'pointer', userSelect: 'none',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                              onClick={() => toggleShowExpand(item.id)}
                            >
                              <Checkbox
                                checked={showAllSel}
                                indeterminate={!showAllSel && showAnySel}
                                onChange={(e) => { e.stopPropagation(); toggleShowEpisodes(showEpKeys); }}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{ p: '2px', flexShrink: 0 }}
                              />
                              <Typography variant="body1" fontWeight={700} sx={{ flex: 1 }}>
                                {item.title}
                              </Typography>
                              <Chip
                                label={`${showEpKeys.length} ep${showEpKeys.length !== 1 ? 's' : ''}`}
                                size="small"
                                sx={{ height: 20, fontSize: '0.65rem', mr: 0.5 }}
                              />
                              <ExpandMoreIcon
                                fontSize="small"
                                sx={{
                                  transition: 'transform 0.2s',
                                  transform: showExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  color: 'text.secondary',
                                }}
                              />
                            </Box>

                            <Collapse in={showExpanded} unmountOnExit>
                              <Divider />
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {sortedSeasons.map(([seasonNumber, episodes], si) => {
                                  const seasonKey = `${item.id}-${seasonNumber}`;
                                  const seasonExpanded = expandedSeasonKeys.has(seasonKey);
                                  const sortedEps = [...episodes].sort((a, b) => a.episodeNumber - b.episodeNumber);
                                  const seasonEpKeys = sortedEps.map(ep =>
                                    `${item.id}-${ep.seasonNumber}-${ep.episodeNumber}`
                                  );
                                  const seasonAllSel = seasonEpKeys.every(k => selectedEpisodeKeys.has(k));
                                  const seasonAnySel = seasonEpKeys.some(k => selectedEpisodeKeys.has(k));

                                  return (
                                    <Box key={seasonKey}>
                                      {si > 0 && <Divider />}
                                      {/* Season header row */}
                                      <Box
                                        sx={{
                                          display: 'flex', alignItems: 'center', gap: 1,
                                          pl: 4, pr: 1.5, py: 0.75,
                                          cursor: 'pointer', userSelect: 'none',
                                          bgcolor: 'action.hover',
                                          '&:hover': { bgcolor: 'action.selected' },
                                        }}
                                        onClick={() => toggleSeasonExpand(seasonKey)}
                                      >
                                        <Checkbox
                                          checked={seasonAllSel}
                                          indeterminate={!seasonAllSel && seasonAnySel}
                                          onChange={(e) => { e.stopPropagation(); toggleSeasonEpisodes(seasonEpKeys); }}
                                          onClick={(e) => e.stopPropagation()}
                                          size="small"
                                          sx={{ p: '2px', flexShrink: 0 }}
                                        />
                                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                                          Season {seasonNumber}
                                        </Typography>
                                        <Chip
                                          label={`${sortedEps.length} ep${sortedEps.length !== 1 ? 's' : ''}`}
                                          size="small"
                                          sx={{ height: 18, fontSize: '0.62rem', mr: 0.5 }}
                                        />
                                        <ExpandMoreIcon
                                          fontSize="small"
                                          sx={{
                                            transition: 'transform 0.2s',
                                            transform: seasonExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            color: 'text.secondary',
                                          }}
                                        />
                                      </Box>

                                      <Collapse in={seasonExpanded} unmountOnExit>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                          {sortedEps.map((episode, ei) => {
                                            const epKey = `${item.id}-${episode.seasonNumber}-${episode.episodeNumber}`;
                                            const isEpSelected = selectedEpisodeKeys.has(epKey);
                                            return (
                                              <Box key={epKey}>
                                                {ei > 0 && <Divider />}
                                                <Box
                                                  sx={{
                                                    display: 'flex', alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 1.5, pl: 7, pr: 1.5, py: 0.75,
                                                    flexWrap: 'wrap',
                                                  }}
                                                >
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Checkbox
                                                      checked={isEpSelected}
                                                      onChange={() => toggleEpisodeKey(epKey)}
                                                      size="small"
                                                      sx={{ p: '2px', flexShrink: 0 }}
                                                    />
                                                    <Box>
                                                      <Typography variant="body2" fontWeight={500}>
                                                        {formatEpisodeLabel(episode)}
                                                        {episode.title ? `: ${episode.title}` : ''}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                        Aired: {formatAirDate(episode.airDate)}
                                                      </Typography>
                                                    </Box>
                                                  </Box>
                                                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                                    <Button
                                                      size="small"
                                                      variant="contained"
                                                      color="success"
                                                      startIcon={<CheckCircleIcon fontSize="small" />}
                                                      onClick={() => handleConfirmEpisode(item, episode)}
                                                      sx={{ fontSize: '0.7rem' }}
                                                    >
                                                      Confirm
                                                    </Button>
                                                    <Button
                                                      size="small"
                                                      variant="outlined"
                                                      color="warning"
                                                      startIcon={<WarningAmberIcon fontSize="small" />}
                                                      onClick={() => handleMarkEpisodeMissing(item, episode)}
                                                      sx={{ fontSize: '0.7rem' }}
                                                    >
                                                      Missing
                                                    </Button>
                                                  </Box>
                                                </Box>
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Paper>
                        );
                      })}
                    </Box>
                  );
                })()}
              </>
            )}
          </Box>
        );
      })()}

      {/* WHITELIST */}
      {activeSection === 2 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Whitelist Management</Typography>

          <Box component="form" onSubmit={handleAddEmail} sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
            <TextField
              type="email"
              size="small"
              placeholder="user@example.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              required
              sx={{ flex: '1 1 240px' }}
            />
            <Button
              type="submit"
              variant="contained"
              color="success"
              startIcon={<PersonAddIcon />}
            >
              Add Email
            </Button>
          </Box>

          {whitelistMsg && <Alert severity="info" sx={{ mb: 2 }}>{whitelistMsg}</Alert>}

          {loadingWhitelist ? (
            <LoadingSpinner message="Loading whitelist…" />
          ) : whitelist.length === 0 ? (
            <Typography color="text.secondary">No users in whitelist.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {whitelist.map(entry => (
                <Paper key={entry.id} sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                  <Typography variant="body2">{entry.email}</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon fontSize="small" />}
                    onClick={() => handleRemoveEmail(entry.email)}
                  >
                    Remove
                  </Button>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* REQUESTS */}
      {activeSection === 3 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>All Requests</Typography>
          {requestsMsg && <Alert severity="info" sx={{ mb: 2 }}>{requestsMsg}</Alert>}
          {loadingRequests ? (
            <LoadingSpinner message="Loading requests…" />
          ) : allRequests.length === 0 ? (
            <Typography color="text.secondary">No requests yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {allRequests.map(req => (
                <Paper key={req.id} sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600}>{req.title}</Typography>
                      <Chip label={req.type === 'tv' ? 'TV' : 'Movie'} size="small" color={req.type === 'tv' ? 'secondary' : 'primary'} sx={{ height: 18, fontSize: '0.65rem' }} />
                      <Chip label={requestStatusLabel(req.status)} size="small" color={requestStatusColor(req.status)} sx={{ height: 18, fontSize: '0.65rem' }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      By {req.requestedBy}
                      {req.requestedAt?.toDate && <> · {req.requestedAt.toDate().toLocaleDateString()}</>}
                    </Typography>
                  </Box>
                  {req.status === 'pending' && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={addingRequest === req.id ? <CircularProgress size={14} color="inherit" /> : <AddIcon fontSize="small" />}
                      disabled={addingRequest === req.id}
                      onClick={() => handleAddRequestClick(req)}
                      sx={{ flexShrink: 0, fontSize: '0.7rem' }}
                    >
                      {addingRequest === req.id ? 'Adding…' : 'Add to Catalogue'}
                    </Button>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ISSUES */}
      {activeSection === 4 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>All Issues</Typography>
          {loadingIssues ? (
            <LoadingSpinner message="Loading issues…" />
          ) : allIssues.length === 0 ? (
            <Typography color="text.secondary">No issues reported.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {allIssues.map(issue => (
                <Paper key={issue.id} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>{issue.title}</Typography>
                    <Chip label={issue.status || 'open'} size="small" color={issueStatusColor(issue.status)} sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 0.75 }}>{issue.description}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reported by {issue.reportedBy}
                    {issue.reportedAt?.toDate && <> · {issue.reportedAt.toDate().toLocaleDateString()}</>}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* MISSING */}
      {activeSection === 5 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Missing Management</Typography>
          {contentMsg && <Alert severity="success" sx={{ mb: 2 }}>{contentMsg}</Alert>}

          {loadingCatalogue ? (
            <LoadingSpinner message="Loading missing content…" />
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 1.5 }}>Missing Titles ({missingItems.length})</Typography>
              {missingItems.length === 0 ? (
                <Typography color="text.secondary" sx={{ mb: 3 }}>No titles marked as missing.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                  {missingItems.map(item => (
                    <MediaCard
                      key={`missing-title-${item.id}`}
                      item={item}
                      onClick={item.type === 'tv' ? () => handleOpenTvEpisodesDialog(item) : undefined}
                      actions={
                        <>
                          {item.type === 'movie' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              fullWidth
                              onClick={() => handleUnmarkMovieMissing(item)}
                              sx={{ fontSize: '0.7rem' }}
                            >
                              Unmark Missing
                            </Button>
                          )}
                          {item.type === 'tv' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              fullWidth
                              onClick={() => handleUnmarkTvShowMissing(item)}
                              sx={{ fontSize: '0.7rem' }}
                            >
                              Unmark Missing
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<DeleteIcon fontSize="small" />}
                            onClick={() => handleRemoveFromCatalogue(item)}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            Remove
                          </Button>
                        </>
                      }
                    />
                  ))}
                </Box>
              )}

              <Typography variant="h6" sx={{ mb: 1.5, mt: 1 }}>
                ⚠️ Missing TV Episodes ({missingTvEpisodes.length})
              </Typography>
              {missingTvEpisodes.length === 0 ? (
                <Typography color="text.secondary" sx={{ mb: 3 }}>No episodes marked as missing.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                  {missingTvEpisodes.map(({ item, episode }) => (
                    <Paper
                      key={`missing-${item.id}-${episode.seasonNumber}-${episode.episodeNumber}`}
                      sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {item.title} — {formatEpisodeLabel(episode)}
                          {episode.title ? `: ${episode.title}` : ''}
                        </Typography>
                        {episode.airDate && (
                          <Typography variant="caption" color="text.secondary">
                            Aired: {formatAirDate(episode.airDate)}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleIcon fontSize="small" />}
                          onClick={() => handleConfirmEpisode(item, episode)}
                          sx={{ fontSize: '0.7rem' }}
                        >
                          Confirm
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          onClick={() => handleUnmarkEpisodeMissing(item, episode)}
                          sx={{ fontSize: '0.7rem' }}
                        >
                          Unmark
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              confirmDialog.onConfirm?.();
              setConfirmDialog(d => ({ ...d, open: false }));
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* TV Episodes Dialog */}
      <Dialog
        open={tvEpisodesDialog.open}
        onClose={() => setTvEpisodesDialog({ open: false, itemId: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          TV Episodes: {selectedTvShow?.title || 'TV Show'}
        </DialogTitle>
        <DialogContent>
          {!selectedTvShow ? (
            <Typography color="text.secondary" sx={{ mt: 1 }}>TV show not found.</Typography>
          ) : selectedTvEpisodes.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 1 }}>No episodes found for this show.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
              {selectedTvEpisodes.map(episode => {
                const label = formatEpisodeLabel(episode);
                const confirmed = isEpisodeConfirmed(episode);
                const missing = isEpisodeMissing(episode);
                return (
                  <Paper
                    key={`${selectedTvShow.id}-${episode.seasonNumber}-${episode.episodeNumber}`}
                    sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {label}{episode.title ? `: ${episode.title}` : ''}
                      </Typography>
                      {episode.airDate && (
                        <Typography variant="caption" color="text.secondary">
                          Aired: {formatAirDate(episode.airDate)}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={missing ? 'Missing' : confirmed ? 'Confirmed' : 'Pending'}
                        size="small"
                        color={missing ? 'warning' : confirmed ? 'success' : 'default'}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon fontSize="small" />}
                        onClick={() => handleConfirmEpisode(selectedTvShow, episode)}
                        sx={{ fontSize: '0.7rem' }}
                      >
                        Confirm
                      </Button>
                      {missing ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          onClick={() => handleUnmarkEpisodeMissing(selectedTvShow, episode)}
                          sx={{ fontSize: '0.7rem' }}
                        >
                          Unmark
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<WarningAmberIcon fontSize="small" />}
                          onClick={() => handleMarkEpisodeMissing(selectedTvShow, episode)}
                          sx={{ fontSize: '0.7rem' }}
                        >
                          Missing
                        </Button>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTvEpisodesDialog({ open: false, itemId: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Movie Dates Dialog */}
      <Dialog
        open={movieDatesDialog.open}
        onClose={() => setMovieDatesDialog(d => ({ ...d, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Movie: {movieDatesDialog.item?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cinema release date is pre-filled from TMDB. Streaming and DVD/Blu-ray dates are pre-filled where available.
          </Typography>
          <TextField
            label="Cinema Release Date"
            type="date"
            size="small"
            fullWidth
            value={
              movieDatesDialog.item?.releaseDate
                ? (typeof movieDatesDialog.item.releaseDate === 'string'
                    ? movieDatesDialog.item.releaseDate
                    : movieDatesDialog.item.releaseDate.toDate().toISOString().slice(0, 10))
                : ''
            }
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Streaming Release Date (optional)"
            type="date"
            size="small"
            fullWidth
            value={movieDatesDialog.streamingDate}
            onChange={e => setMovieDatesDialog(d => ({ ...d, streamingDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="DVD / Blu-ray Release Date (optional)"
            type="date"
            size="small"
            fullWidth
            value={movieDatesDialog.dvdDate}
            onChange={e => setMovieDatesDialog(d => ({ ...d, dvdDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMovieDatesDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={handleMovieDatesSubmit}>
            Add Movie
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Movie Dates Dialog */}
      <Dialog
        open={editDatesDialog.open}
        onClose={() => setEditDatesDialog(d => ({ ...d, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Dates: {editDatesDialog.item?.title}</DialogTitle>
        <DialogContent>
          <TextField
            label="Cinema Release Date"
            type="date"
            size="small"
            fullWidth
            value={editDatesDialog.cinemaDate}
            onChange={e => setEditDatesDialog(d => ({ ...d, cinemaDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            label="Streaming Release Date (optional)"
            type="date"
            size="small"
            fullWidth
            value={editDatesDialog.streamingDate}
            onChange={e => setEditDatesDialog(d => ({ ...d, streamingDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="DVD / Blu-ray Release Date (optional)"
            type="date"
            size="small"
            fullWidth
            value={editDatesDialog.dvdDate}
            onChange={e => setEditDatesDialog(d => ({ ...d, dvdDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDatesDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={handleEditDatesSubmit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Admin;